import type * as monacoType from "monaco-editor";
import SysColumn from "app/class/sys-column";
import { CodeEditorProfile } from "./types";

type Monaco = typeof monacoType;
type TranslateCodeEditorText = (key: string, fallback?: string) => string;

const SORT_PREFIX = {
    contextual: "000",
    snippet: "0000",
};

const DB_WHERE_TYPES = [
    { value: "condition", detail: "Single field condition" },
    { value: "logical", detail: "AND/OR condition group" },
    { value: "exists", detail: "EXISTS query against another table" },
];

const DB_QUERY_OPERATORS = [
    { value: "eq", detail: "Equals" },
    { value: "neq", detail: "Not equals" },
    { value: "in", detail: "Matches any value in an array" },
    { value: "contains", detail: "Contains text" },
    { value: "not_contains", detail: "Does not contain text" },
    { value: "stw", detail: "Starts with" },
    { value: "edw", detail: "Ends with" },
    { value: "gt", detail: "Greater than" },
    { value: "gte", detail: "Greater than or equal" },
    { value: "lt", detail: "Less than" },
    { value: "lte", detail: "Less than or equal" },
    { value: "between", detail: "Between two values" },
    { value: "empty", detail: "Null or empty" },
    { value: "not_empty", detail: "Not null and not empty" },
    { value: "eq_dynamic", detail: "Equals a runtime source value" },
    { value: "neq_dynamic", detail: "Does not equal a runtime source value" },
    { value: "gt_dynamic", detail: "Greater than a runtime source value" },
    { value: "gte_dynamic", detail: "Greater than or equal to a runtime source value" },
    { value: "lt_dynamic", detail: "Less than a runtime source value" },
    { value: "lte_dynamic", detail: "Less than or equal to a runtime source value" },
];

const DB_LOGICAL_OPERATORS = [
    { value: "and", detail: "All child conditions must match" },
    { value: "or", detail: "Any child condition may match" },
];

const DB_ORDER_DIRECTIONS = [
    { value: "asc", detail: "Ascending" },
    { value: "desc", detail: "Descending" },
];

const disposableGroup = (items: monacoType.IDisposable[]): monacoType.IDisposable => ({
    dispose: () => items.forEach((item) => item.dispose()),
});

const getCompletionRange = (
    model: monacoType.editor.ITextModel,
    position: monacoType.Position
) => {
    const word = model.getWordUntilPosition(position);

    return {
        startLineNumber: position.lineNumber,
        endLineNumber: position.lineNumber,
        startColumn: word.startColumn,
        endColumn: word.endColumn,
    };
};

const getStringLiteralCompletionContext = (
    model: monacoType.editor.ITextModel,
    position: monacoType.Position
) => {
    const linePrefix = model.getLineContent(position.lineNumber).slice(0, position.column - 1);
    const match = linePrefix.match(/([A-Za-z_$][\w$]*)\s*:\s*(["']?)([^"',}]*)$/);

    if (!match) {
        return null;
    }

    const [, property, quote, typedValue] = match;
    const startsInsideString = Boolean(quote);
    const startColumn = position.column - typedValue.length - (startsInsideString ? 0 : 0);

    return {
        property,
        typedValue: typedValue.toLocaleLowerCase(),
        startsInsideString,
        range: {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn,
            endColumn: position.column,
        },
    };
};

const getTableNameCompletionContext = (
    model: monacoType.editor.ITextModel,
    position: monacoType.Position
) => {
    const linePrefix = model.getLineContent(position.lineNumber).slice(0, position.column - 1);
    const match = linePrefix.match(/(?:\b(?:client\.)?db\s*\.\s*)?table\s*\(\s*(["']?)([^"')]*)$/);

    if (!match) {
        return null;
    }

    const [, quote, typedValue] = match;
    const startsInsideString = Boolean(quote);

    return {
        typedValue: typedValue.toLocaleLowerCase(),
        startsInsideString,
        range: {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: position.column - typedValue.length,
            endColumn: position.column,
        },
    };
};

const getFormFieldCompletionContext = (
    model: monacoType.editor.ITextModel,
    position: monacoType.Position
) => {
    const linePrefix = model.getLineContent(position.lineNumber).slice(0, position.column - 1);
    const match = linePrefix.match(/\b(?:Gate\s*\.\s*)?form\s*\.\s*(?:getValue|setValue|setVisible|setRequired|setReadonly|setDisabled|setLabel|setOptions|addFieldError|clearFieldError|addFieldInfo|clearFieldInfo|onFieldChange)\s*\(\s*(["']?)([^"')]*)$/);

    if (!match) {
        return null;
    }

    const [, quote, typedValue] = match;
    const startsInsideString = Boolean(quote);

    return {
        typedValue: typedValue.toLocaleLowerCase(),
        startsInsideString,
        range: {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: position.column - typedValue.length,
            endColumn: position.column,
        },
    };
};

const getEventFieldCompletionContext = (
    model: monacoType.editor.ITextModel,
    position: monacoType.Position
) => {
    const linePrefix = model.getLineContent(position.lineNumber).slice(0, position.column - 1);
    const match = linePrefix.match(/\bevent\s*\.\s*field\s*(?:={1,3}|!==?)\s*(["']?)([^"']*)$/);

    if (!match) {
        return null;
    }

    const [, quote, typedValue] = match;
    const startsInsideString = Boolean(quote);

    return {
        typedValue: typedValue.toLocaleLowerCase(),
        startsInsideString,
        range: {
            startLineNumber: position.lineNumber,
            endLineNumber: position.lineNumber,
            startColumn: position.column - typedValue.length,
            endColumn: position.column,
        },
    };
};

const getSnippetPrefix = (
    model: monacoType.editor.ITextModel,
    position: monacoType.Position
) => {
    const word = model.getWordUntilPosition(position);

    if (word.word) {
        return word.word.toLocaleLowerCase();
    }

    return model.getValue().trim() ? null : "";
};

const translateText = (translate: TranslateCodeEditorText, key: string, fallback?: string) =>
    translate(key, fallback) || fallback;

const toJsDoc = (value: string | null | undefined) =>
    (value || "")
        .split("/")
        .join("\\/")
        .split("*")
        .join("")
        .split("\r")
        .join(" ")
        .split("\n")
        .join(" ");

const typeForColumn = (column: SysColumn) => {
    switch (column.column_type) {
        case "BOOLEAN":
            return "boolean";
        case "INTEGER":
        case "DECIMAL":
        case "CURRENCY":
            return "number";
        case "DATE":
        case "DATETIME":
        case "TIME":
        case "UUID":
        case "TEXT":
        case "CHOICE":
        case "CODE":
        case "RICH_HTML":
        case "TABLE_NAME":
            return "string";
        default:
            return "unknown";
    }
};

const isAlphaNumeric = (character: string) => {
    const code = character.charCodeAt(0);

    return (
        (code >= 48 && code <= 57) ||
        (code >= 65 && code <= 90) ||
        (code >= 97 && code <= 122)
    );
};

const toPascalCase = (value: string) => {
    let converted = "";
    let shouldUppercase = true;

    Array.from(value).forEach((character) => {
        if (!isAlphaNumeric(character)) {
            shouldUppercase = true;
            return;
        }

        converted += shouldUppercase ? character.toLocaleUpperCase() : character;
        shouldUppercase = false;
    });

    return converted || "Record";
};

const typeNameForTable = (tableName: string) => `Primum${toPascalCase(tableName)}Record`;

const isClientActionProfile = (profile: CodeEditorProfile) =>
    profile.id.startsWith("form_action.") ||
    profile.id.startsWith("client_script.") ||
    profile.id === "default_value.script";

const isBackendDbProfile = (profile: CodeEditorProfile) =>
    profile.id.startsWith("business_rule.") ||
    profile.id.startsWith("platform_job.") ||
    profile.id === "dynamic_api.handler";

const buildRecordInterface = (
    typeName: string,
    columns: SysColumn[],
    translate: TranslateCodeEditorText,
    tableLabel?: string
) => {
    const lines = [
        `/** ${toJsDoc(tableLabel ? `${tableLabel} table record.` : "Platform table record.")} */`,
        `interface ${typeName} extends PrimumRecord {`,
    ];

    columns.forEach((column) => {
        const label = column.label ? translate(column.label, column.label) : column.name;
        const description = [
            label,
            column.column_type,
            column.referenced_table_name ? `Reference to ${column.referenced_table_name}` : "",
        ].filter(Boolean).join(" - ");

        lines.push(`  /** ${toJsDoc(description)} */`);
        lines.push(`  ${JSON.stringify(column.name)}?: ${typeForColumn(column)};`);
    });

    lines.push("}");

    return lines.join("\n");
};

const buildTableSchemasLib = (profile: CodeEditorProfile, translate: TranslateCodeEditorText) => {
    const schemas = profile.tableSchemas ?? [];

    if (!schemas.length) {
        return "";
    }

    const tableTypeNames = new Map<string, string>();
    const usedTypeNames = new Set<string>();
    const uniqueSchemas = schemas.filter((schema) => {
        if (!schema.name || tableTypeNames.has(schema.name)) {
            return false;
        }

        const baseTypeName = typeNameForTable(schema.name);
        let typeName = baseTypeName;
        let suffix = 2;

        while (usedTypeNames.has(typeName)) {
            typeName = `${baseTypeName}${suffix}`;
            suffix += 1;
        }

        tableTypeNames.set(schema.name, typeName);
        usedTypeNames.add(typeName);

        return true;
    });

    const recordInterfaces = uniqueSchemas.map((schema) =>
        buildRecordInterface(
            tableTypeNames.get(schema.name) || typeNameForTable(schema.name),
            schema.columns,
            translate,
            schema.label || schema.name
        )
    );

    const buildDbOverloads = (
        interfaceName: "PrimumDb" | "ClientDb",
        tableApiTypeName: "PrimumDbTable" | "ClientDbTable"
    ) => [
        `interface ${interfaceName} {`,
        ...uniqueSchemas.map((schema) =>
            [
                `  /** Selects ${toJsDoc(schema.label || schema.name)} records. */`,
                `  table(name: ${JSON.stringify(schema.name)}): ${tableApiTypeName}<${tableTypeNames.get(schema.name) || typeNameForTable(schema.name)}>;`,
            ].join("\n")
        ),
        "}",
    ];
    const overloads = [];

    if (isBackendDbProfile(profile)) {
        overloads.push(buildDbOverloads("PrimumDb", "PrimumDbTable").join("\n"));
    }

    if (isClientActionProfile(profile)) {
        overloads.push(buildDbOverloads("ClientDb", "ClientDbTable").join("\n"));
    }

    return [...recordInterfaces, ...overloads].join("\n\n");
};

const buildTargetRecordLib = (profile: CodeEditorProfile, translate: TranslateCodeEditorText) => {
    const columns = profile.targetColumns ?? [];
    const targetTableTypeName = profile.targetTable?.name
        ? typeNameForTable(profile.targetTable.name)
        : "PrimumRecord";

    if (!columns.length) {
        return [
            "/** Target table fields resolved from the current record metadata. */",
            "interface PrimumTargetRecord extends PrimumRecord {}",
        ].join("\n");
    }

    const lines = [
        "/** Target table fields resolved from the current record metadata. */",
        `interface PrimumTargetRecord extends ${targetTableTypeName} {`,
    ];

    columns.forEach((column) => {
        const label = column.label ? translate(column.label, column.label) : column.name;
        const description = [
            label,
            column.column_type,
            column.referenced_table_name ? `Reference to ${column.referenced_table_name}` : "",
        ].filter(Boolean).join(" - ");

        lines.push(`  /** ${toJsDoc(description)} */`);
        lines.push(`  ${JSON.stringify(column.name)}?: ${typeForColumn(column)};`);
    });

    lines.push("}");

    return lines.join("\n");
};

const typeForProcessVariable = (type: string) => {
    switch (String(type || "").toUpperCase()) {
        case "TEXT": case "STRING": case "CHOICE": case "EMAIL": case "URL":
        case "UUID": case "REFERENCE": case "DATE": case "DATETIME": case "TIME":
            return "string";
        case "INTEGER": case "NUMBER": case "DECIMAL": case "FLOAT": case "CURRENCY":
            return "number";
        case "BOOLEAN":
            return "boolean";
        default:
            return "unknown";
    }
};

const buildProcessVariablesLib = (profile: CodeEditorProfile) => profile.processVariables === undefined
    ? ""
    : ["interface FlussProcessVariables {",
        ...profile.processVariables.map(({ key, type }) =>
            `  /** ${toJsDoc(type)} */ ${JSON.stringify(key)}?: ${typeForProcessVariable(type)};`),
        "}"].join("\n");

export const buildExtraLib = (profile: CodeEditorProfile, translate: TranslateCodeEditorText) =>
    [
        profile.extraLib,
        buildTableSchemasLib(profile, translate),
        buildTargetRecordLib(profile, translate),
        buildProcessVariablesLib(profile),
    ].filter(Boolean).join("\n\n");

const uniqueFieldNamesForProfile = (profile: CodeEditorProfile) => {
    const names = new Set<string>();

    (profile.targetColumns || []).forEach((column) => {
        if (column.name) {
            names.add(column.name);
        }
    });

    (profile.tableSchemas || []).forEach((schema) => {
        (schema.columns || []).forEach((column) => {
            if (column.name) {
                names.add(column.name);
            }
        });
    });

    return Array.from(names).sort((left, right) => left.localeCompare(right));
};

const contextualValuesForProperty = (profile: CodeEditorProfile, property: string) => {
    switch (property) {
        case "type":
            return DB_WHERE_TYPES;
        case "operator":
            return [...DB_QUERY_OPERATORS, ...DB_LOGICAL_OPERATORS];
        case "direction":
            return DB_ORDER_DIRECTIONS;
        case "field":
            return uniqueFieldNamesForProfile(profile).map((value) => ({
                value,
                detail: "Field from loaded table metadata",
            }));
        default:
            return [];
    }
};

const tableNameValuesForProfile = (profile: CodeEditorProfile) =>
    (profile.tableSchemas || [])
        .filter((schema) => schema.name)
        .map((schema) => ({
            value: schema.name,
            detail: schema.label || "Platform table",
        }));

const buildTableNameCompletions = (
    monaco: Monaco,
    profile: CodeEditorProfile,
    model: monacoType.editor.ITextModel,
    position: monacoType.Position
) => {
    const context = getTableNameCompletionContext(model, position);

    if (!context) {
        return [];
    }

    return tableNameValuesForProfile(profile)
        .filter((item) => item.value.toLocaleLowerCase().startsWith(context.typedValue))
        .map((item) => ({
            label: item.value,
            kind: monaco.languages.CompletionItemKind.Value,
            detail: item.detail,
            insertText: context.startsInsideString ? item.value : JSON.stringify(item.value),
            sortText: `${SORT_PREFIX.contextual}_table_${item.value}`,
            range: context.range,
        }));
};

const buildContextualCompletions = (
    monaco: Monaco,
    profile: CodeEditorProfile,
    model: monacoType.editor.ITextModel,
    position: monacoType.Position
) => {
    const context = getStringLiteralCompletionContext(model, position);

    if (!context) {
        return [];
    }

    return contextualValuesForProperty(profile, context.property)
        .filter((item) => item.value.toLocaleLowerCase().startsWith(context.typedValue))
        .map((item) => ({
            label: item.value,
            kind: monaco.languages.CompletionItemKind.Value,
            detail: item.detail,
            insertText: context.startsInsideString ? item.value : JSON.stringify(item.value),
            sortText: `${SORT_PREFIX.contextual}_${context.property}_${item.value}`,
            range: context.range,
        }));
};

const buildFormFieldCompletions = (
    monaco: Monaco,
    profile: CodeEditorProfile,
    model: monacoType.editor.ITextModel,
    position: monacoType.Position
) => {
    if (!profile.id.startsWith("client_script.") && profile.id !== "service_portal.form_script") {
        return [];
    }

    const context = getFormFieldCompletionContext(model, position) || getEventFieldCompletionContext(model, position);

    if (!context) {
        return [];
    }

    return (profile.targetColumns || [])
        .filter((column) => column.name)
        .filter((column) => column.name.toLocaleLowerCase().startsWith(context.typedValue))
        .map((column) => ({
            label: column.name,
            kind: monaco.languages.CompletionItemKind.Field,
            detail: column.label || "Current table field",
            insertText: context.startsInsideString ? column.name : JSON.stringify(column.name),
            sortText: `${SORT_PREFIX.contextual}_form_field_${column.order ?? 0}_${column.name}`,
            range: context.range,
        }));
};

export const configureCodeEditor = (
    monaco: Monaco,
    profile: CodeEditorProfile | null,
    modelUri: string,
    translate: TranslateCodeEditorText = (_key, fallback) => fallback || ""
): monacoType.IDisposable => {
    if (!profile) {
        return disposableGroup([]);
    }

    monaco.languages.typescript.javascriptDefaults.setCompilerOptions({
        ...monaco.languages.typescript.javascriptDefaults.getCompilerOptions(),
        allowNonTsExtensions: true,
        checkJs: true,
        noLib: false,
    });

    monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions({
        ...monaco.languages.typescript.javascriptDefaults.getDiagnosticsOptions(),
        noSemanticValidation: false,
        noSyntaxValidation: false,
    });

    const extraLib = monaco.languages.typescript.javascriptDefaults.addExtraLib(
        buildExtraLib(profile, translate),
        `file:///primum/code-editor/${profile.id}.d.ts`
    );

    const completions = monaco.languages.registerCompletionItemProvider(profile.language, {
        triggerCharacters: [".", "\"", "'", "="],
        provideCompletionItems: (model, position) => {
            if (model.uri.toString() !== modelUri) {
                return { suggestions: [] };
            }

            const range = getCompletionRange(model, position);
            const snippetPrefix = getSnippetPrefix(model, position);
            const contextualSuggestions = [
                ...buildTableNameCompletions(monaco, profile, model, position),
                ...buildContextualCompletions(monaco, profile, model, position),
                ...buildFormFieldCompletions(monaco, profile, model, position),
            ];

            if (snippetPrefix === null) {
                return { suggestions: contextualSuggestions };
            }

            const snippets = profile.snippets.filter((snippet) =>
                snippet.label.toLocaleLowerCase().startsWith(snippetPrefix)
            );

            return {
                suggestions: [
                    ...contextualSuggestions,
                    ...snippets.map((snippet) => ({
                        label: snippet.label,
                        kind: monaco.languages.CompletionItemKind.Snippet,
                        detail: translateText(
                            translate,
                            snippet.detailKey || `codeEditor.snippet.${snippet.label}.detail`,
                            snippet.detail || profile.label
                        ),
                        documentation: translateText(
                            translate,
                            snippet.documentationKey || `codeEditor.snippet.${snippet.label}.documentation`,
                            snippet.documentation
                        ),
                        insertText: snippet.insertText,
                        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
                        sortText: `${SORT_PREFIX.snippet}_${snippet.label}`,
                        range,
                    })),
                ],
            };
        },
    });

    return disposableGroup([extraLib, completions]);
};
